import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Shield, Heart, Brain, Users, AlertTriangle, CheckCircle2 } from 'lucide-react';

const safetyProtocols = [
  {
    icon: Shield,
    title: "Medical Screening",
    description: "Comprehensive health assessment before every ceremony to ensure participant safety and identify any contraindications."
  },
  {
    icon: Brain,
    title: "Mental Health Support",
    description: "Professional psychological support available throughout your journey, including pre and post-ceremony consultations."
  },
  {
    icon: Heart,
    title: "Certified Facilitators",
    description: "Our team includes certified facilitators trained in both traditional practices and modern emergency response protocols."
  },
  {
    icon: Users,
    title: "Small Groups",
    description: "We maintain intimate group sizes to ensure personalized attention and support for each participant."
  }
];

const guidelines = [
  "Follow all dietary restrictions for 2 weeks prior",
  "Disclose all medications and health conditions",
  "Arrive with an open heart and clear intentions",
  "Respect the sacred space and other participants",
  "Follow facilitator guidance throughout the ceremony",
  "Commit to the integration process afterward"
];

const contraindications = [
  "Heart conditions or high blood pressure",
  "Current use of SSRIs or MAOIs",
  "Pregnancy or breastfeeding",
  "Severe mental health conditions",
  "Recent surgery or major illness"
];

export const SafetySection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="safety" className="py-24 relative" data-testid="safety-section">
      {/* Background Accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neon-cyan/5 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-neon-cyan text-sm tracking-[0.3em] mb-4">
            YOUR SAFETY IS SACRED
          </p>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight text-white mb-6">
            Security <span className="neon-gold">Protocols</span>
          </h2>
          <p className="font-body text-gray-400 text-lg max-w-3xl mx-auto">
            We combine ancient wisdom with modern safety standards. Your wellbeing is our highest priority, 
            and we maintain rigorous protocols to ensure a safe, transformative experience.
          </p>
        </motion.div>

        {/* Safety Protocols Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {safetyProtocols.map((protocol, index) => {
            const Icon = protocol.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-2xl p-6 text-center card-hover"
                data-testid={`safety-protocol-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-neon-cyan/10 flex items-center justify-center mx-auto mb-4 border border-neon-cyan/30">
                  <Icon className="w-7 h-7 text-neon-cyan" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wide mb-3">
                  {protocol.title}
                </h3>
                <p className="font-body text-gray-400 text-sm leading-relaxed">
                  {protocol.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Guidelines and Contraindications */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Guidelines */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="glass-card rounded-3xl p-8 border border-green-500/20"
            data-testid="safety-guidelines"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wide">
                Preparation Guidelines
              </h3>
            </div>
            <ul className="space-y-4">
              {guidelines.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="font-body text-gray-300 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contraindications */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="glass-card rounded-3xl p-8 border border-yellow-500/20"
            data-testid="safety-contraindications"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
              <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wide">
                Contraindications
              </h3>
            </div>
            <p className="font-body text-gray-400 text-sm mb-4">
              Please consult with us if you have any of the following:
            </p>
            <ul className="space-y-4">
              {contraindications.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span className="font-body text-gray-300 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-12 text-center"
        >
          <p className="font-body text-gray-500 text-sm max-w-2xl mx-auto">
            Our commitment to safety never compromises the authenticity of the experience. 
            All protocols are designed in harmony with traditional practices while meeting 
            international health and safety standards.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
